import { useAuth } from 'contexts/auth';
import { useSnackbar } from 'contexts/snackbar';
import { createContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { emailRegex } from 'utils/regex';

const defaultForm = {
    email: '',
    code: '',
    password: '',
    confirmPassword: '',
};

const PAGES = {
    ENTER_EMAIL: 'enter-email',
    EMAIL_SENT: 'email-sent',
    NEW_PASSWORD: 'new-password',
    SUCCESS: 'success',
};

const PageContext = createContext();

const PageProvider = (props) => {
    const location = useLocation();
    const { message } = useSnackbar();
    const { forgotPassword, confirmForgotPassword, verifyUser } = useAuth();
    const { t } = useTranslation();

    const [loading, setLoading] = useState(false);
    const [form, setForm] = useState(defaultForm);
    const [errors, setErrors] = useState({
        'enter-email': {
            email: '',
        },
        'new-password': {
            code: '',
            password: '',
            confirmPassword: '',
        },
    });
    const [currentStep, setCurrentStep] = useState(PAGES.ENTER_EMAIL);
    const [satisfyRequirement, setSatisfyRequirement] = useState({
        validateMoreThan8Characters: true,
        validateOneUpperCase: true,
        validateOneLowerCase: true,
        validateOneNumber: true,
        validateOneSpecial: true,
    });

    const getErrorMessage = (key, value, form) => {
        value = value?.trim();
        switch (key) {
            case 'email':
                return value && emailRegex.test(value) ? '' : t('errors.invalid-email', 'Please enter a valid email');
            case 'password':
                if (Object.values(satisfyRequirement).find((requirement) => requirement))
                    return t(
                        'account-settings.password.please-satisfy-requirements',
                        'Please satisfy all requirements'
                    );
                return value ? '' : t('account-settings.password.enter-password', 'Please enter password');
            case 'confirmPassword':
                return form.password === value
                    ? ''
                    : t('account-settings.password.passwords-mismatch', 'Passwords do not match');
            case 'code':
                return value ? '' : t('errors.valid-code', 'Enter a valid code');
            default:
                return '';
        }
    };

    const handleSendForgotPasswordCode = () => {
        if (!validateAll()) return;
        setLoading(true);
        forgotPassword(form.email?.toLowerCase()).finally(() => setLoading(false));
        handleNext(PAGES.NEW_PASSWORD);
        return message.success(
            t(
                'message.password-email-sent',
                'If this email is registered with an account, you will be receiving an email soon.'
            )
        );
    };

    const handleConfirmationCode = async (code) => {
        return verifyUser(form.email?.toLowerCase(), code);
    };

    const handleUpdatePassword = async () => {
        if (!validateAll()) return;
        setLoading(true);
        try {
            await confirmForgotPassword(form.email?.toLowerCase(), form.code, form.password);
            message.success(t('message.password-reset-success', 'Your password was reset.'));
            setCurrentStep(PAGES.SUCCESS);
        } catch (e) {
            const codeError =
                e?.name === 'ExpiredCodeException'
                    ? t('errors.code-expired', 'The code has expired; please resend the email')
                    : t('errors.valid-code', 'Enter a valid code');
            setErrors((current) => ({
                ...current,
                [PAGES.NEW_PASSWORD]: { ...current[PAGES.NEW_PASSWORD], code: codeError },
            }));
        }
        setLoading(false);
    };

    const handleResendCode = async () => {
        message.success(
            t(
                'message.password-email-sent',
                'If this email is registered with an account, you will be receiving an email soon.'
            )
        );
        return await forgotPassword(form.email?.toLowerCase());
    };

    const handleChange = async (key, data) => {
        switch (key) {
            case 'email': {
                return setForm((prev) => ({ ...prev, email: data }));
            }
            case 'password': {
                return setForm((prev) => ({ ...prev, password: data }));
            }
            case 'confirm-password': {
                return setForm((prev) => ({ ...prev, confirmPassword: data }));
            }
            case 'code': {
                return setForm((prev) => ({ ...prev, code: data }));
            }
            default:
                return;
        }
    };

    const handleNext = (step) => {
        setCurrentStep(step);
    };

    const handleNewPasswordRequirement = (password) => {
        const atLeastOneUpperCase = /^.*[A-Z]+.*$/;
        const atLeastOneLowerCase = /^.*[a-z]+.*$/;
        const atLeastOneNumber = /^.*[0-9]+.*$/;
        const atLeast8Character = /^[a-zA-Z0-9!"#$%&'()*+,-./:;<=>?@[\]^_`{|}~]{8,20}$/;
        const atLeastOneSpecial = /^(?=.*[!@#$%^&*]).*$/;
        const validateOneUpperCase = atLeastOneUpperCase.exec(password);
        const validateOneLowerCase = atLeastOneLowerCase.exec(password);
        const validateOneNumber = atLeastOneNumber.exec(password);
        const validateMoreThan8Characters = atLeast8Character.exec(password);
        const validateOneSpecial = atLeastOneSpecial.exec(password);
        setSatisfyRequirement({
            validateOneUpperCase: !validateOneUpperCase ? true : false,
            validateOneLowerCase: !validateOneLowerCase ? true : false,
            validateOneNumber: !validateOneNumber ? true : false,
            validateMoreThan8Characters: !validateMoreThan8Characters ? true : false,
            validateOneSpecial: !validateOneSpecial ? true : false,
        });
    };

    const validate = (key, value) => {
        const errorMessage = getErrorMessage(key, value, form);
        setErrors((errors) => {
            return {
                ...errors,
                [currentStep]: { ...errors[currentStep], [key]: errorMessage },
            };
        });
    };

    const validateAll = () => {
        return !Boolean(
            Object.keys(errors[currentStep])
                .map((key) => {
                    validate(key, form[key]);
                    return getErrorMessage(key, form[key], form);
                })
                .reduce((cur, acc) => {
                    return cur + acc;
                }, '')
        );
    };

    useEffect(() => {
        if (location.state?.email) {
            setForm((prev) => ({ ...prev, email: location.state.email }));
        }
    }, [location.state]);

    useEffect(() => {
        if (form.email?.length > 0) {
            setForm((prev) => {
                return { ...prev, email: form.email };
            });
        }
    }, [form?.email]);

    useEffect(() => {
        handleNewPasswordRequirement(form.password);
    }, [form?.password, form?.confirmPassword]);

    return (
        <PageContext.Provider
            value={{
                loading,
                form,
                errors,
                currentStep,
                satisfyRequirement,

                handleChange,
                handleNext,
                handleConfirmationCode,
                handleSendForgotPasswordCode,
                handleUpdatePassword,
                handleResendCode,
                setErrors,
                setForm,
                setLoading,
                PAGES,
            }}
            {...props}
        />
    );
};

export { PageContext };
export default PageProvider;
